import type {
	ChartSelectorOptions,
	MobileActiveSection,
	PortfolioMenuItemSelection
} from '$lib/types/home';
import type {
	FundingRateInterval,
	Market,
	MarketIdentifiers,
	MarketSortKeys,
	MarketTimeseriesPoint,
	MarketTimeseriesRangeOption
} from '$lib/types/market';
import type { Order, PaginatedOrders } from '$lib/types/order';
import type { Position } from '$lib/types/position';
import type { Token, TokenSymbol } from '$lib/types/tokens';
import type { PaginatedTrades } from '$lib/types/trade';
import type { PostTradeDetails } from '$lib/types/userInteract';
import { writable, derived, type Writable, type Readable, get } from 'svelte/store';
import { browser } from '$app/environment';
import type { PythPrice } from '$lib/types/pyth';
import BigNumber from 'bignumber.js';
import {
	DEFAULT_MAKER_BASIS_POINTS,
	DEFAULT_MAX_LEVERAGE,
	DEFAULT_TAKER_BASIS_POINTS
} from '$lib/notifications/constants';
import { marketsConfig } from '$lib/config/markets';
import {
	getPositionFromOrder,
	getTakeProfitAndStopLossFromOrder,
	isEntirePositionTPSLOrder,
	isOrderInProcess,
	isValidTPSLOrderForPosition
} from './util';
import { tokensAllowedForDeposit } from './scwallet/config';
import { positionsRaw } from '$store/positions';
import { ordersRaw } from '$store/orders';

import type { IMatchaResponse, IStablesPrices, LiquidityProviderSources } from '$lib/web3/types';
import { POLYNOMIAL_FEE, SWAP_SLIPPAGE } from '$lib/web3/contants';
import { AUTOSWAP_OP, AUTOSWAP_STABLES } from '$lib/settings';
import persistentStorage from '$lib/persistentStorage';

export const loadingUserBalances: Writable<boolean> = writable(true);
export const firstLoadedUserBalances: Writable<boolean> = writable(false);
export const userBalances: Writable<Record<TokenSymbol, Token> | Record<string, never>> =
	persistentStorage('user-balances', {});

export const loadingNativeUserBalances: Writable<boolean> = writable(false);
export const nativeUserBalances: Writable<Record<TokenSymbol, Token>> = writable();

export const loadingAuxillaryBalance: Writable<boolean> = writable(false);

export const marketSortKey: Writable<MarketSortKeys> = writable('volume24h');
export const marketSortDirection: Writable<'Ascending' | 'Descending'> = writable('Descending');
export const markets: Writable<Array<Market>> = writable();
export const sortedMarkets: Readable<Market[]> = derived(
	[markets, marketSortKey, marketSortDirection],
	([$markets, $marketSortKey, $marketSortDirection]) => {
		if ($markets && $marketSortKey && $marketSortDirection) {
			let tempMarkets: Market[] = [];
			$markets.forEach((market: Market) => {
				tempMarkets.push(market);
			});
			tempMarkets = tempMarkets.sort((a: Market, b: Market) => {
				if ($marketSortDirection == 'Descending')
					return new BigNumber(b?.[$marketSortKey] ?? 0).minus(a?.[$marketSortKey] ?? 0).toNumber();
				else {
					return new BigNumber(a?.[$marketSortKey] ?? 0).minus(b?.[$marketSortKey] ?? 0).toNumber();
				}
			});
			return tempMarkets;
		} else {
			return [];
		}
	}
);

export const fetchingMarkets: Writable<boolean> = writable(false);
export const currentMarketIdentifier: Writable<MarketIdentifiers> = writable('sETHPERP');
export const totalMarketTrades: Writable<number> = writable(0);
export const totalMarketOpenInterest: Writable<string> = writable('0');
export const totalMarketVolume: Writable<string> = writable('0');

export const currentMarket: Readable<Market | undefined> = derived(
	[markets, currentMarketIdentifier],
	([$markets, $currentMarketIdentifier]) => {
		if ($markets && $markets.length > 0) {
			// return the market with the same identifier as the current market
			return $markets.find((e) => e.marketIdentifier == $currentMarketIdentifier);
		} else {
			return undefined;
		}
	}
);

export const currentMarketAsset = derived([currentMarket], ([$currentMarket]) => {
	if ($currentMarket) {
		if ($currentMarket.marketIdentifier == 'sETHPERP') {
			return 'ETH';
		} else if ($currentMarket.marketIdentifier == 'sBTCPERP') {
			return 'BTC';
		} else return $currentMarket.asset;
	} else {
		return undefined;
	}
});

export const maxLeverage = derived([currentMarket], ([$currentMarket]) => {
	let lev = DEFAULT_MAX_LEVERAGE;
	if ($currentMarket) {
		lev = marketsConfig[$currentMarket.asset].maxLeverage;
	}
	return lev;
});

export const currentMakerBasisPoints = derived([currentMarket], ([$currentMarket]) => {
	let pts = DEFAULT_MAKER_BASIS_POINTS;
	if ($currentMarket) {
		pts = marketsConfig[$currentMarket.asset].makerBasisPoints;
	}
	return pts;
});

export const currentTakerBasisPoints = derived([currentMarket], ([$currentMarket]) => {
	let pts = DEFAULT_TAKER_BASIS_POINTS;
	if ($currentMarket) {
		pts = marketsConfig[$currentMarket.asset].takerBasisPoints;
	}
	return pts;
});

export const userHoldingActiveItem: Writable<string> = writable('positions');

// export const positions: Writable<Array<Position>> = writable();
// export const firstTimeFetchingPositions: Writable<boolean> = writable(true);
// export const fetchingPositions: Writable<boolean> = writable(false);

export const positions = positionsRaw.store;

export const currentPosition: Readable<Position | undefined> = derived(
	[positions, currentMarketIdentifier],
	([$positions, $currentMarketIdentifier]) => {
		if ($positions && $positions.data && $positions.data.length > 0) {
			//todo Assumpion here is that user only has 1 positions since only ETh market is there
			return $positions.data.find((e) => e.marketIdentifier == $currentMarketIdentifier);
		} else {
			return undefined;
		}
	}
);
// export const orders: Writable<Array<Order>> = writable();
export const orders = ordersRaw.store;
export const limitOrders: Readable<Array<Order>> = derived([orders], ([$orders]) => {
	if ($orders && $orders.data && $orders.data.length > 0)
		return $orders.data.filter((e) => e.orderType == 'Limit');
	else return [];
});
export const ordersObject: Readable<Record<string, Order>> = derived([orders], ([$orders]) => {
	let obj: Record<string, Order> = {};
	if ($orders && $orders.data && $orders.data.length > 0) {
		$orders.data.forEach((e) => {
			obj[e.orderId] = e;
		});
		return obj;
	} else {
		return obj;
	}
});

export const recentFewOrderHistory: Writable<PaginatedOrders> = writable();

export const paginatedOrderHistory: Writable<PaginatedOrders> = writable();
export const currentPageOrderHistory: Writable<number> = writable(1);
export const fetchingPaginatedOrderHistory: Writable<boolean> = writable(false);

export const ordersBeingTracked: Writable<Record<string, Order>> = writable({});
export const tpslBeingUpdated: Writable<Array<string>> = writable([]);

export const unsubmittedOrders: Writable<Record<string, Order>> = writable(
	JSON.parse(browser ? localStorage.getItem('unsubmittedOrdersV3') || '{}' : '{}')
);
if (browser)
	unsubmittedOrders.subscribe(
		(value) => (localStorage.unsubmittedOrdersV3 = JSON.stringify(value))
	);

export const ordersToShow: Readable<Array<Order>> = derived(
	[orders, unsubmittedOrders],
	([$orders, $unsubmittedOrders]) => {
		let uniqueOrdersObj: Record<string, Order> = {};

		if ($unsubmittedOrders && Object.keys($unsubmittedOrders).length > 0) {
			Object.entries($unsubmittedOrders).forEach(([key, value]) => {
				uniqueOrdersObj[key.toLowerCase()] = value;
			});
		}
		if ($orders && $orders.data && $orders.data.length > 0) {
			$orders.data.forEach((e) => {
				uniqueOrdersObj[e.orderIdUi.toLowerCase()] = e;
			});
		}
		let orders: Array<Order> = [];
		for (let key in uniqueOrdersObj) {
			orders.push(uniqueOrdersObj[key]);
		}
		return orders;
	}
);

export const positionsBeingCreated: Readable<Array<Position>> = derived(
	[positions, ordersToShow],
	([$positions, $ordersToShow]) => {
		let positionsBeingCreatedArr: Array<Position> = [];

		if ($positions && $ordersToShow) {
			$ordersToShow.forEach((e) => {
				if (isOrderInProcess(e) && $positions.data) {
					if ($positions.data.filter((val) => val.asset == e.asset).length == 0) {
						positionsBeingCreatedArr.push(getPositionFromOrder(e));
					}
				}
			});
		}
		return positionsBeingCreatedArr;
	}
);

export const positionsBeingUpdated: Readable<Array<string>> = derived(
	[positions, ordersToShow],
	([$positions, $ordersToShow]) => {
		let positionsBeingUpdatedArr: Array<string> = [];
		if ($positions && $ordersToShow) {
			$ordersToShow.forEach((e) => {
				if (isOrderInProcess(e) && $positions.data) {
					if ($positions.data.filter((val) => val.asset == e.asset).length > 0) {
						positionsBeingUpdatedArr.push(e.asset);
					}
				}
			});
		}
		return positionsBeingUpdatedArr;
	}
);

export const positionsToShow: Readable<Array<Position>> = derived(
	[positions, positionsBeingCreated, orders],
	([$positions, $positionsBeingCreated, $orders]) => {
		let positionsTemp: Array<Position> = [];
		if ($positions && $positions.data && $orders && $orders.data) {
			$positions.data.forEach((position) => {
				let noOfTpsl = 0;
				$orders.data?.forEach((order) => {
					if (isValidTPSLOrderForPosition(order, position)) {
						if (isEntirePositionTPSLOrder(order)) {
							const isLong = new BigNumber(position.size).isPositive();
							const { takeProfit, stopLoss } = getTakeProfitAndStopLossFromOrder(order, isLong);
							position.takeProfit = takeProfit;
							position.stopLoss = stopLoss;
							position.tpslOrderId = order.orderId.toString();
						}
						noOfTpsl++;
					}
				});
				position.noOfTpsl = noOfTpsl;
				positionsTemp.push(position);
			});
		}
		if ($positions && $positions.data && $positionsBeingCreated) {
			$positionsBeingCreated.forEach((beingCreated) => {
				if ($positions.data && $positions.data.length > 0) {
					$positions.data.forEach((position) => {
						if (beingCreated.marketIdentifier !== position.marketIdentifier) {
							positionsTemp.push(beingCreated);
						}
					});
				} else {
					positionsTemp.push(beingCreated);
				}
			});
		}
		return positionsTemp;
	}
);

export const isPositionInSameMarketPresent: Readable<boolean> = derived(
	[positionsToShow, currentMarketIdentifier],
	([$positionsToShow, $currentMarketIdentifier]) => {
		if ($positionsToShow && $positionsToShow.length > 0) {
			for (let i = 0; i < $positionsToShow.length; i++) {
				if ($positionsToShow[i].marketIdentifier === $currentMarketIdentifier) {
					return true;
				}
			}
		}
		return false;
	}
);
export const paginatedTrades: Writable<PaginatedTrades> = writable();
export const currentPageTrades: Writable<number> = writable(1);
export const fetchingPaginatedData: Writable<boolean> = writable(false);

export const firstTimeFetchingTrades: Writable<boolean> = writable(true);
export const fetchingTrades: Writable<boolean> = writable(false);

export const loadingPostTradeDetails: Writable<boolean> = writable(false);
export const postTradeDetails: Writable<PostTradeDetails> = writable();

export const fundingRateInterval: Writable<FundingRateInterval> = writable('1H');
export const mobileActiveSection: Writable<MobileActiveSection> = writable('Positions');

const defaultValue = true;
const initialValue = browser
	? Boolean(window.localStorage.getItem('access')) ?? defaultValue
	: defaultValue;
export const accessCodeVerified: Writable<boolean> = writable(initialValue);

export const accessCodeInput: Writable<string> = writable('');
export const verifyingAccessCode: Writable<boolean> = writable(false);

export const accessCodeWrong: Readable<boolean> = derived(
	[accessCodeVerified, accessCodeInput, verifyingAccessCode],
	([$accessCodeVerified, $accessCodeInput, $verifyingAccessCode]) => {
		if (
			$verifyingAccessCode == false &&
			$accessCodeInput.length == 6 &&
			$accessCodeVerified == false
		) {
			return true;
		} else {
			return false;
		}
	}
);

export const tradingViewChartReady: Writable<boolean> = writable(false);

//pyth prices
export const pythPrices: Writable<Record<string, PythPrice>> = writable();
export const fetchingPythPrices: Writable<boolean> = writable(false);

// stables prices
export const stablesPrices: Writable<IStablesPrices[]> = writable();
export const loadedStablePricesSuccessfully: Writable<boolean> = writable(false);

export const portfolioMenuItemSelected: Writable<PortfolioMenuItemSelection> = writable('Overview');

//exnriched balances
export const userBalancesEnriched: Readable<Record<string, Token>> = derived(
	[userBalances, pythPrices],
	([$userBalances, $pythPrices]) => {
		if ($userBalances && $pythPrices) {
			let enrichedUserBalances: Record<string, Token> = {};
			Object.keys($userBalances).forEach((e) => {
				enrichedUserBalances[e] = {
					...$userBalances[e as TokenSymbol],
					price: $pythPrices[e]
						? new BigNumber($pythPrices[e].price)
								.multipliedBy(`1e${$userBalances[e as TokenSymbol].decimals}`)
								.div(`1e${$pythPrices[e].decimals}`)
								.toFixed(0)
						: '0',
					balanceUSD: $pythPrices[e]
						? new BigNumber($userBalances[e as TokenSymbol].balance)
								.multipliedBy($pythPrices[e].price)
								.div(`1e${$pythPrices[e].decimals}`)
								.toFixed(0)
						: '0'
				};
			});
			return enrichedUserBalances;
		} else if ($userBalances) {
			return $userBalances;
		} else {
			return {};
		}
	}
);

export const nativeBalancesEnriched: Readable<Record<string, Token>> = derived(
	[nativeUserBalances, pythPrices],
	([$nativeUserBalances, $pythPrices]) => {
		if ($nativeUserBalances && $pythPrices) {
			let enrichedNativeuserBalances: Record<string, Token> = {};
			Object.keys($nativeUserBalances).forEach((e) => {
				enrichedNativeuserBalances[e] = {
					...$nativeUserBalances[e as TokenSymbol],
					price: $pythPrices[e]
						? new BigNumber($pythPrices[e].price)
								.multipliedBy(`1e${$nativeUserBalances[e as TokenSymbol].decimals}`)
								.div(`1e${$pythPrices[e].decimals}`)
								.toFixed(0)
						: '0',
					balanceUSD: $pythPrices[e]
						? new BigNumber($nativeUserBalances[e as TokenSymbol].balance)
								.multipliedBy($pythPrices[e].price)
								.div(`1e${$pythPrices[e].decimals}`)
								.toFixed(0)
						: '0'
				};
			});
			return enrichedNativeuserBalances;
		} else if ($nativeUserBalances) {
			return $nativeUserBalances;
		} else {
			return {};
		}
	}
);

export const chartSelected: Writable<ChartSelectorOptions> = writable('TradingView');
export const marketTimeSeriesPoints: Writable<Array<MarketTimeseriesPoint>> = writable();
export const loadingMarketTimeSeriesPoints: Writable<boolean> = writable(false);
export const marketTimeSeriesRange: Writable<MarketTimeseriesRangeOption> = writable('1D');

//exnriched balances
export const tokensAllowedForDepositBalance: Readable<Record<string, Token>> = derived(
	[userBalancesEnriched, tokensAllowedForDeposit],
	([$userBalancesEnriched, $tokensAllowedForDeposit]) => {
		let tokensForDepositInfo: Record<string, Token> = {};
		$tokensAllowedForDeposit.forEach((element: string) => {
			tokensForDepositInfo[element] = {
				...$userBalancesEnriched[element]
			};
		});

		return tokensForDepositInfo;
	}
);

export const loadingMatchaCallData: Writable<boolean> = writable(false);
export const matchaCallData: Writable<Record<string, IMatchaResponse>> = writable({});
export const amountPerTokenToSwap: Writable<Record<string, BigNumber>> = writable();

export const matchaProtocolFee: Readable<BigNumber> = derived(
	[matchaCallData],
	([$matchaCallData]) => {
		let totalProtocolFee: BigNumber = new BigNumber('0');
		if ($matchaCallData && Object.keys($matchaCallData).length !== 0) {
			Object.keys($matchaCallData).forEach((token) => {
				if ($matchaCallData[token] && $matchaCallData[token].protocolFee) {
					totalProtocolFee = new BigNumber($matchaCallData[token].buyAmount)
						.multipliedBy(POLYNOMIAL_FEE)
						.plus(totalProtocolFee);
				}
			});
		}
		return totalProtocolFee;
	}
);

AUTOSWAP_OP.subscribe((value) => {
	if (!value) {
		matchaCallData.set({});
	}
});

AUTOSWAP_STABLES.subscribe((value) => {
	if (!value) {
		matchaCallData.set({});
	}
});

export const swapSlippage: Writable<string> = writable(SWAP_SLIPPAGE);

export const aggregatedSwapMarginPossible: Readable<BigNumber> = derived(
	[matchaCallData, tokensAllowedForDeposit],
	([$matchaCallData, $tokensAllowedForDeposit]) => {
		let totalMarginFromSwap: BigNumber = new BigNumber('0');
		if ($matchaCallData && Object.keys($matchaCallData).length !== 0) {
			$tokensAllowedForDeposit.forEach((token) => {
				if ($matchaCallData['sUSD']) {
					return;
				}

				if ($matchaCallData[token]) {
					totalMarginFromSwap = new BigNumber($matchaCallData[token].buyAmount).plus(
						totalMarginFromSwap
					);
				}
			});
		}
		return totalMarginFromSwap;
	}
);

export const liquidityProviderForSwap: Readable<Record<string, LiquidityProviderSources[]>> =
	derived(
		[matchaCallData, tokensAllowedForDeposit],
		([$matchaCallData, $tokensAllowedForDeposit]) => {
			let providersForSwap: Record<string, LiquidityProviderSources[]> = {};
			if ($matchaCallData && Object.keys($matchaCallData).length !== 0) {
				$tokensAllowedForDeposit.forEach((token) => {
					if ($matchaCallData['sUSD']) {
						return;
					}

					if (
						$matchaCallData[token] &&
						$matchaCallData[token].sources &&
						$matchaCallData[token].sources.length > 0
					) {
						providersForSwap[token] = $matchaCallData[token].sources.filter((val) => {
							return parseFloat(val.proportion) > 0;
						});
					}
				});
			}

			return providersForSwap;
		}
	);
